import React, { useState } from "react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import UploadFileIcon from "./UploadFileIcon";
import FieldMapping from "./FieldMapping";

interface FileUploadProps {
    onDataParsed: (data: any[], tableHeaders: string[]) => void;
    accept: string;
    fieldMappings: Record<string, string>;
    onFieldMappingChange: (updatedMappings: Record<string, string>) => void;
    requiredFields: string[];
    optionalFields?: string[];
}

const FileUpload: React.FC<FileUploadProps> = ({
                                                   onDataParsed,
                                                   accept,
                                                   fieldMappings,
                                                   onFieldMappingChange,
                                                   requiredFields,
                                                   optionalFields = [],
                                               }) => {
    const [ fileName, setFileName ] = useState<string | null>(null);
    const [ error, setError ] = useState<string | null>(null);
    const [ headers, setHeaders ] = useState<string[]>([]);
    const [ rawData, setRawData ] = useState<Record<string, string>[]>([]);

    const processRowData = (row: Record<string, string>, headersFromSheet?: string[], mappings?: Record<string, string>) => {
        const processedRow: Record<string, any> = {};
        const currentMappings = mappings || fieldMappings; // Use provided mappings or default to state
        requiredFields.forEach((field) => {
            const csvField = currentMappings[field]; // Use the provided or state mappings
            processedRow[field] = csvField && (!headersFromSheet || headersFromSheet.includes(csvField))
                ? row[csvField] || ""
                : "";
        });

        optionalFields.forEach((field) => {
            const csvField = currentMappings[field];
            processedRow[field] = csvField && (!headersFromSheet || headersFromSheet.includes(csvField))
                ? row[csvField] || ""
                : "";
        });
        return processedRow;
    };

    const handleFieldMappingChange = (updatedMappings: Record<string, string>) => {
        // Update the field mappings state
        onFieldMappingChange(updatedMappings);

        // Use the updated mappings to reprocess raw data
        const reprocessedData = rawData.map((row) => processRowData(row, headers, updatedMappings));
        onDataParsed(reprocessedData, headers);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setFileName(file.name);
            setError(null);

            const reader = new FileReader();

            reader.onload = (event) => {
                const result = event.target?.result;
                if (result) {
                    if (file.name.endsWith(".csv")) {
                        Papa.parse(result as string, {
                            header: true,
                            skipEmptyLines: true,
                            complete: (results: any) => {
                                const detectedHeaders = results.meta.fields || [];
                                setHeaders(detectedHeaders);
                                setRawData(results.data);

                                const processedData = results.data.map((row: Record<string, string>) =>
                                    processRowData(row),
                                );
                                onDataParsed(processedData, detectedHeaders);
                            },
                            error: () => setError("Failed to parse CSV file."),
                        });
                    } else if (file.name.endsWith(".xlsx")) {
                        try {
                            const workbook = XLSX.read(result, { type: "binary" });
                            const sheetName = workbook.SheetNames[0];
                            const sheet = workbook.Sheets[sheetName];

                            // Extract data as 2D array
                            const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as Array<string[]>;

                            // Extract headers and rows
                            const detectedHeaders = jsonData[0].map((header) =>
                                header?.trim() || "",
                            ) as string[]; // Normalize headers
                            const rows = jsonData.slice(1); // Skip header row

                            setHeaders(detectedHeaders);

                            const rawData = rows.map((row) => {
                                return detectedHeaders.reduce((acc, header, index) => {
                                    acc[header] = row[index] || "";
                                    return acc;
                                }, {} as Record<string, string>);
                            });
                            setRawData(rawData);
                            const processedData = rawData.map((row) => processRowData(row, detectedHeaders));
                            onDataParsed(processedData, detectedHeaders);
                        } catch (error) {
                            console.error("Error parsing Excel file:", error);
                            setError("Failed to parse Excel file.");
                        }
                    }
                }
            };

            reader.onerror = () => {
                setError("Failed to read file.");
            };

            reader.readAsBinaryString(file);
        }
    };

    return (
        <div>
            <div
                className={`py-4 flex flex-col items-center gap-4 rounded-lg`}>
                <label
                    htmlFor="file-upload"
                    className="flex flex-col items-center justify-center w-full h-32 sm:h-40 border-2 border-dashed border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 rounded-lg cursor-pointer hover:border-gray-500 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                >
                    <UploadFileIcon/>
                    <input id="file-upload" type="file" accept={accept} onChange={handleFileChange}
                           className="hidden"/>
                </label>
                {fileName && (
                    <div className="text-xs sm:text-sm text-center">
                        Selected file: <strong>{fileName}</strong>
                    </div>
                )}
                {error && <div className="text-xs sm:text-sm text-red-500 text-center">{error}</div>}
                {headers.length > 0 && (
                    <FieldMapping
                        headers={headers}
                        fieldMappings={fieldMappings}
                        requiredFields={requiredFields}
                        optionalFields={optionalFields}
                        onFieldMappingChange={handleFieldMappingChange}
                    />
                )}
            </div>
        </div>
    );
};

export default FileUpload;